<template>
  <div class="main-product">
    <product-gallery
      class="main-product__gallery"
      :is-mobile="isMobile"
      :parent-state="states.product"
      :product="product"
      :show-stl-button="showShopTheLook"
    />

    <div
      ref="productDetails"
      class="main-product__details"
    >
      <product-form
        class="main-product__form"
        :enable-size-guide="enableSizeGuide"
        :is-mobile="isMobile"
        :liquid="liquid"
        :parent-current-variant="currentVariant"
        :parent-state="states.product"
        :product="product"
        :show-product-media="showMainFormPackShot"
        :show-swatches="showSwatches"
        @update-current-product="handleProductUpdate"
      />

      <product-description
        class="main-product__description"
        :parent-state="states.product"
        :product="product"
        :usp-list="uspList"
        :delivery-returns-title="liquid.deliveryReturns.title"
        :delivery-returns-body="liquid.deliveryReturns.body"
      />

      <div
        v-if="showLiveShopping"
        class="main-product__live-shopping"
      >
        <bambuser-button
          class="main-product__live-shopping--thumb"
          :show-id="liveShoppingID"
        >
          <responsive-image
            class="main-product__live-shopping--image"
            aspect-ratio="0.728"
            fetch-priority="low"
            loading="lazy"
            min-max="200-400"
            :image="liveShoppingImage"
          />
          <button
            class="main-product__live-shopping--play-btn"
            :aria-label="liveShoppingButtonText"
          >
            <icon-play />
          </button>
        </bambuser-button>

        <bambuser-button
          class="main-product__live-shopping--cta link link--underline"
          :show-id="liveShoppingID"
          v-text="liveShoppingButtonText"
        />
      </div>

      <div
        v-if="showShopTheLook"
        class="main-product__shop-the-look"
      >
        <h2
          id="shop-the-look"
          class="main-product__shop-the-look-heading"
          v-text="shopTheLookHeading"
        />

        <div
          v-for="(ctlProduct, index) in product.completeTheLook.products"
          :key="`${ctlProduct.key}-${index}`"
        >
          <product-form
            class="main-product__shop-the-look-form"
            :is-mobile="isMobile"
            :parent-state="states.product"
            :product="ctlProduct"
            type="shop-the-look"
          />

          <hr class="main-product__shop-the-look-separator">
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Section: Main Product.
 * ------------------------------------------------------------------------------
 * - Section for housing the Product Gallery, Form and Description.
 * @param {Boolean} enableSizeGuide - Enable the Size Guide button or not.
 * @param {String} liquid.product - Formatted Liquid product object.
 * @param {String} liquid.sectionId - Sections Liquid ID.
 * @param {Boolean} showInitialShopTheLook - Display STL during initial loading skeleton.
 * @param {Boolean} showMainFormPackShot - Display pack shot on the main product form. This is disabled when there is no 'Shop The Look' * This is disabled when there is no 'Shop The Look'
 * @param {Boolean} showSwatches - Display swatches on initial loading skeleton for main product form.
 * @param {Object} uspList - USP list sourced from metaobjects.
 * @param {Object} [bambuserTrackingParameters] - Object of parameters used for Bambuser tracking.
 */
import { mapActions, mapState } from 'pinia'

import { useProductStore } from '~stores/product'
import { useScreenStore } from '~stores/screen'
import { useWishlistStore } from '~stores/wishlist'

import ProductForm from '~components/product-page/product-form/product-form'
import ProductGallery from '~components/product-gallery/product-gallery'
import ProductDescription from '~components/product-description/product-description'

import ResponsiveImage from '~components/responsive-image/responsive-image'
import IconPlay from '~icons/media/play.svg'

import BambuserButton from '~components/bambuser-button/bambuser-button'

export default {
  name: 'MainProduct',

  components: {
    ProductForm,
    ProductGallery,
    ProductDescription,
    ResponsiveImage,
    IconPlay,
  },

  props: {
    enableSizeGuide: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        deliveryReturns: {
          title: '',
          body: '',
        },
        sectionId: '',
      }),
    },
    bambuserTrackingParameters: {
      type: Object,
      default: () => ({}),
    },
    showSwatches: {
      type: Boolean,
      default: false,
    },
    showInitialShopTheLook: {
      type: Boolean,
      default: false,
    },
    showMainFormPackShot: {
      type: Boolean,
      default: false,
    },
    uspList: {
      type: [Boolean, Object],
      default: () => {},
    },
  },

  data() {
    return {
      states: {
        product: 'initialLoad',
      },
    }
  },

  computed: {

    /**
     * Map Product data.
     */
    ...mapState(useProductStore, {
      completeTheLook: 'completeTheLook',
      currentVariant: 'currentVariant',
      flexiFashion: 'flexiFashion',
      product: 'productData',
    }),

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Map global state.
     */
    ...mapState(useWishlistStore, ['wishlistReady']),

    /**
     * Calculate if to display additional 'Shop The Look' forms.
     * - Display when loading as most products will have STL
     * @returns {Boolean}
     */
    showShopTheLook() {
      if (this.states.product === 'initialLoad') {
        return this.showInitialShopTheLook
      }

      return Boolean(this.product.completeTheLook?.products?.length)
    },

    /**
     * Calculate the heading text for 'Shop The Look'.
     * @returns {String}
     */
    shopTheLookHeading() {
      return mint.strings.productDetailPage.shopTheLook.heading
    },

    /**
     * Calculate the `Live Shopping ID` value.
     * @returns {String|Boolean}
     */
    liveShoppingID() {
      return this.product.liveShopping?.id || false
    },

    /**
     * Calculate the `Live Shopping CTA` text.
     * @returns {String}
     */
    liveShoppingButtonText() {
      return (
        this.product.liveShopping?.cta ||
          mint.Strings('products.product_detail_page.live_shopping.cta')
      )
    },

    /**
     * Calculate the `Live Shopping` image thumb.
     * @returns {Object|Boolean}
     */
    liveShoppingImage() {
      if (this.product.liveShopping?.image) {
        return this.product.liveShopping?.image
      }

      if (this.product.images?.length) {
        return this.product.images[0]
      }

      return false
    },

    /**
     * Calculate if to display additional 'Live Shopping' button / thumbnail.
     * @returns {Boolean}
     */
    showLiveShopping() {
      if (!this.product?.wishlist?.bisEnabled && !this.product?.available) {
        return false
      }

      return Boolean(this.liveShoppingID)
    },
  },

  watch: {

    /**
     * The product has been updated.
     * - Will execute on initial page load
     * - Will execute on swatch update.
     * - If you want to debug the loading/skeleton phase:
     * - Change 'ready' to 'initialLoad/loading'
     * - And/or head to store/product.js and comment out
     *   `this.productData = product` in `setProduct`
     */
    'product.key': {
      handler(newValue, oldValue) {
        if (!this.product.handle) {
          this.states.product = 'loading'
          return
        }

        this.states.product = 'ready'
      },
      immediate: true,
    },
  },

  mounted() {

    /**
     * Components and state should initialise in `product.key` watcher, as this
     * key is set once the product is actually ready.
     */

    this.setDelayedSticky()
    this.initialiseBambuser()
  },

  methods: {

    /**
     * Handle variant updates.
     */
    ...mapActions(useProductStore, [
      'updateProduct',
      'updateLinkedStyle',
      'updateCurrentProduct',
    ]),

    /**
     * The customer has interacted with a swatch or variant selector.
     * - Product Form component emits an event, containing either a
     *   `linkedStyle` or `variant`
     * - Pinia will update the product and return it to this component.
     * @param {Object} payload - Swatch style 'Colour' object
     * @param {Object} [payload.linkedStyle] - Object containing new linked style handle.
     * @param {Object} [payload.variant] - Formatted product variant object
     */
    handleProductUpdate(payload) {
      if (payload.linkedStyle) {
        this.states.product = 'loading'
      }

      this.updateCurrentProduct(payload)
    },

    /**
     * Sets a delayed sticky behavior to a specified element.
     *
     * - since the `productDetails` contain a different top position from 0,
     * the jumps during loading can be quite noticeable
     * - this function is designed to address potential jolts during page load
     * when applying sticky positioning to an element
     * - it ensures that the sticky class is applied after a short delay, allowing
     * the browser to stabilize the layout and reduce jolting effects
     */
    setDelayedSticky() {
      setTimeout(() => {
        this.$refs.productDetails.classList.add('is-sticky')
      }, 500)
    },

    /**
     * Conditionally imports and initialises the Bambuser Api (and button).
     * - This triggers if the `bambuser.show_id` metafield is set
     */
    initialiseBambuser() {
      if (!this.liveShoppingID) {
        return
      }

      import(
        /* webpackChunkName: 'component.bambuser-api' */
        '~/api/bambuser'
      ).then((module) => {
        const Bambuser = module.default

        Bambuser.init() // This also initialises the `BambuserButton` component
        Bambuser.subscribeOnBambuserEvents({
          preventDrawer: true,
          trackingParameters: this.bambuserTrackingParameters,
        })
      })
    },
  },
}
</script>

<style lang="scss">
  @import './main-product';
  @import '~components/product-description/product-description';
  @import '~components/product-gallery/product-gallery';
  @import '~components/product-page/product-form/product-form';
</style>
